import React, { useCallback, useEffect, useMemo, useState } from "react";

import _ from "lodash";
import cx from "classnames";

import styles from "./persona.module.scss";
import Button from "src/components/button";
import { SimulationContext } from "../../../context";
import { ChatContainer } from "../../chat";
import { useSelector } from "react-redux";
import Thumbnail from "src/components/thumbnail";
import TextareaAutosize from "react-autosize-textarea";
import { Persona, Personality, Simulation, SimulationValidationError, Voice } from "src/types/coaching/simulation";
import { useDispatch } from "react-redux";
import { fetchPersonalities, fetchVoices, generateSimulationSummary, invalidateGenerateSimulationSummaryState, updateSimulationProperty } from "src/redux/actions/coaching/simulation";
import { Input } from "src/components/input";
import useMeasure from "react-use-measure";
import { Spring, animated, useTransition } from "@react-spring/web";
import ScrollToBottom from 'react-scroll-to-bottom';


import AsyncSelect from "react-select/async";
import { OptionProps, components } from "react-select";
import { Media, MediaFile } from "src/types/media";
import { toast } from "react-toastify";
import { parseMediaMetadata } from "src/utils/media.service";
import { invalidateMediaUpload, uploadMedia } from "src/redux/actions/media";
import Tooltip from "src/components/tooltip";
import { preparePersonaRequest, validateSimulationRequest } from "../../../helper";

interface PersonaEditorProps {
  simulation: Simulation;
}

export const PersonaEditor: React.FC<PersonaEditorProps> = ({ simulation }) => {
  const dispatch = useDispatch();
  const simulationContext = React.useContext(SimulationContext);

  const persistedPersona = simulation?.persona;
  const [persona, setPersona] = useState<Persona | undefined>(persistedPersona!);
  const [errors, setErrors] = useState<SimulationValidationError[]>([]);

  const icon = persona?.icon;
  const name = persona?.name || "";
  const description = persona?.description || "";

  const profileSummary = persona?.profile_summary || "";
  const designation = persona?.designation || "";
  const voice = persona?.voice || null;
  const personality = persona?.personality || null;

  const objectiveTendencies = persona?.objective_tendencies || "";
  const exampleStatements = persona?.example_statements || "";
  const smallTalk = persona?.small_talk || "";
  const successMetrics = persona?.success_metrics || "";

  const title = useMemo(() => _.get(simulation, 'description'), [simulation])
  const { uploading, uploaded, media } = useSelector((state: any) => {
    const entityType = _.get(icon, 'entityType');
    if (entityType === 'media-preview') {
      const uploading = _.get(state, `media['${icon?.id}'].loading`, false);
      const uploaded = _.get(state, `media['${icon?.id}'].loaded`, false);
      const media = _.get(state, `media['${icon?.id}'].item`);

      return {
        uploading,
        uploaded,
        media,
      }
    }

    return {
      uploading: false,
      uploaded: false,
      media: null,
    };
  }, _.isEqual);

  const { generatingSummary, generatedSummary } = useSelector((state: any) => {
    return {
      generatingSummary: _.get(state, `coaching.simulations.edit.generateSummary.generating`, false),
      generatedSummary: _.get(state, `coaching.simulations.edit.generateSummary.generated`, false),
    }
  }, _.isEqual);

  const validationErrors = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.edit.errors`) as SimulationValidationError[];
  }, _.isEqual);

  const nameError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "name" }) || null;
  }, [errors]);

  const descriptionError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "description" }) || null;
  }, [errors]);

  const profileSummaryError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "profile_summary" }) || null;
  }, [errors]);

  const designationError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "designation" }) || null;
  }, [errors]);

  const objectiveTendenciesError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "objective_tendencies" }) || null;
  }, [errors]);

  const personalityError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "personality" }) || null;
  }, [errors]);

  const voiceError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "voice" }) || null;
  }, [errors]);

  const exampleStatementsError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "example_statements" }) || null;
  }, [errors]);

  const smallTalkError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "small_talk" }) || null;
  }, [errors]);

  const successMetricsError = useMemo(() => {
    return _.find(errors, { type: "persona", property: "success_metrics" }) || null;
  }, [errors]);

  useEffect(() => {
    setPersona(persistedPersona!);
  }, [persistedPersona!]);

  useEffect(() => {
    dispatch(fetchVoices({
      page: 0,
      limit: 25,
    }));
  }, []);

  useEffect(() => {
    if (uploaded) {
      const mediaId = _.get(icon, 'id');
      handlePropertyChange("icon", media);

      if (mediaId) {
        dispatch(invalidateMediaUpload(mediaId));
      }
    }
  }, [icon, uploaded, media]);

  useEffect(() => {
    if(generatedSummary){
      dispatch(invalidateGenerateSimulationSummaryState(simulation.id!));
      
      if (simulationContext.editPersona) {
        simulationContext.editPersona(false);
      }
    }
  }, [generatedSummary]);

  const handlePropertyChange = (property: string, value: any) => {
    setPersona((prevPersona) => {
      const updatedPersona = {
        ...prevPersona || {},
        [property]: value
      } as Persona;
      return updatedPersona;
    });
  };

  const handleIconChange = async (file: File) => {
    const profileIcon = await parseMediaMetadata(file);
    handlePropertyChange("icon", profileIcon);

    dispatch(uploadMedia({
      id: profileIcon?.id as string,
      media: {
        ...profileIcon,
        file: file
      } as MediaFile
    }));
  }

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handlePropertyChange("name", event.target.value);
  }

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("description", event.target.value);
  }

  const handleProfileSummaryChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("profile_summary", event.target.value);
  }

  const handleDesignationChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("designation", event.target.value);
  }

  const handleObjectiveTendenciesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("objective_tendencies", event.target.value);
  }

  const handlePersonalityChange = (personality: any) => {
    handlePropertyChange("personality", personality);
  }

  const handleVoiceChange = (voice: any) => {
    handlePropertyChange("voice", voice);
  }

  const handleExampleStatementsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("example_statements", event.target.value);
  }

  const handleSmallTalkChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("small_talk", event.target.value);
  }

  const handleSuccessMetricsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("success_metrics", event.target.value);
  }

  const handleConfirm = () => {
    const changes = preparePersonaRequest({
      name: "",
      description: "",
      profile_summary: "",
      designation: "",
      objective_tendencies: "",
      example_statements: "",
      small_talk: "",
      success_metrics: "",
      voice: {
        id: "",
        name: "",
        description: "",
        tags: [],
        previewUrl: "",
      },
      personality: {
        id: "",
        name: "",
        description: "",
        type: "",
      },
      icon: {
        id: "",
        type: "image"
      },
    }, persona);
    
    dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "persona", value: persona }));

    dispatch(generateSimulationSummary({
      simulation: simulation.id!,
      description: simulation.description || "",
      persona: changes
    }));
  }

  const handleCancel = () => {
    if (simulationContext.editPersona) {
      simulationContext.editPersona(false);
    }
  }

  const handleGetParameters = () => {
    const parameters = {
      name: name,
      description: description,
      profile_summary: profileSummary,
      designation: designation,
      objective_tendencies: objectiveTendencies,
      example_statements: exampleStatements,
      small_talk: smallTalk,
      success_metrics: successMetrics,
    };

    return parameters;
  }

  const handleParametersChange = (parameters: any) => {
    _.keys(parameters).forEach((key) => {
      const value = parameters[key];
      if (value) {
        handlePropertyChange(key, value);
      }
    });
  }

  useEffect(() => {
    if (persona) {
      const simulationRequest = {
        ...simulation,
        persona: persona
      }

      const validationErrors = validateSimulationRequest(simulationRequest);
      setErrors(validationErrors);
    }
  }, [persona]);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title} onClick={handleCancel}>
          <button className={styles.backButton}>
            <i className={cx(styles.backIcon, 'paperflite-left-arrow')} />
          </button>
          <h2>Scenario</h2>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" className={cx(styles.action, styles.confirm)}
            icon="paperflite-tick" text={generatingSummary ? "Confirming..." : "Confirm"}
            disabled={uploading || generatingSummary}
            onClick={handleConfirm} />
          <Button className={cx(styles.action, styles.cancel)}
            icon="paperflite-cancel"
            text="Cancel"
            disabled={uploading || generatingSummary}
            onClick={handleCancel}
          />
        </div>
      </div>
      <p className={styles.description}>Edit your scenario</p>

      <div className={styles.body}>
        <ScrollToBottom className={cx(styles.left, styles.chat)} scrollViewClassName={styles.scrollView} followButtonClassName={styles.scrollButton}>
          <div className={styles.header}>
            <h4>Need help with the brief?</h4>
          </div>
          <div className={styles.chats}>
            <ChatContainer simulation={simulation}
              configurationType="persona"
              getParameters={handleGetParameters}
              onChange={handleParametersChange}
            />
          </div>
        </ScrollToBottom>
        <div className={cx(styles.right, styles.persona)}>
          <div className={styles.summaryView}>
            <div className={styles.profile}>
              <ProfileIcon icon={icon} onChange={handleIconChange} />
            </div>
            <Input className={styles.name}
              value={name}
              placeholder="Enter persona name..."
              onChange={handleNameChange}
            />
            <TextareaAutosize
              className={styles.summary}
              rows={6}
              value={description}
              placeholder={'Type here the description...'}
              spellCheck={true}
              onChange={handleDescriptionChange}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </div>
          <div className={styles.formView}>
            <div className={styles.field}>
              <FieldLabel label="Designation" error={designationError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={designation}
                placeholder={'Signals authority/influence level...'}
                spellCheck={true}
                onChange={handleDesignationChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Profile" error={profileSummaryError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={profileSummary}
                placeholder={'High-level job description or background...'}
                spellCheck={true}
                onChange={handleProfileSummaryChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <PersonalityField personality={personality} onChange={handlePersonalityChange} />
            <VoiceField voice={voice} onChange={handleVoiceChange} />
            <div className={styles.field}>
              <FieldLabel label="Objection Tendencies" error={objectiveTendenciesError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={objectiveTendencies}
                placeholder={'Prepares reps for likely friction points...'}
                spellCheck={true}
                onChange={handleObjectiveTendenciesChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Example Statements" error={exampleStatementsError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={exampleStatements}
                placeholder={'Common phrases or attitudes they express...'}
                spellCheck={true}
                onChange={handleExampleStatementsChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Small Talk" error={smallTalkError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={smallTalk}
                placeholder={'For rapport-building practice...'}
                spellCheck={true}
                onChange={handleSmallTalkChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Success Metrics (KPIs)" error={successMetricsError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={successMetrics}
                placeholder={'What this persona is measured on...'}
                spellCheck={true}
                onChange={handleSuccessMetricsChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ProfileIconProps {
  icon: Media | undefined | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (file: File) => void;
}

const ProfileIcon: React.FC<ProfileIconProps> = ({ icon, onChange }) => {
  const { uploading, uploaded, media } = useSelector((state: any) => {
    const entityType = _.get(icon, 'entityType');
    if (entityType === 'media-preview') {
      const uploading = _.get(state, `media['${icon?.id}'].loading`, false);
      const uploaded = _.get(state, `media['${icon?.id}'].loaded`, false);
      const media = _.get(state, `media['${icon?.id}'].item`);

      return {
        uploading,
        uploaded,
        media,
      }
    }

    return {
      uploading: false,
      uploaded: false,
      media: null,
    };
  }, _.isEqual);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 2MB");
      return;
    }

    onChange(file);
  }

  return (
    <div className={cx(styles.profileIcon)}>
      <Thumbnail media={icon} defaultImage="/images/assets/simulationThumbnail.jpg" className={styles.icon} />
      <div className={styles.actions}>
        <button className={cx(styles.action, styles.edit)}>
          <i className='paperflite-upload' />
          <input type="file" accept={'image/*'}
            disabled={uploading}
            onChange={handleFileChange}
          ></input>
        </button>
      </div>
    </div>
  );
}

interface VoiceFieldProps {
  voice: Voice | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (voice: Voice) => void;
}

const VoiceField: React.FC<VoiceFieldProps> = ({ voice, onChange }) => {
  const [edit, setEdit] = useState(false);
  const [bodyRef, bodyBounds] = useMeasure();
  const [height, setHeight] = useState(0);
  const [previewVoice, setPreviewVoice] = useState<string | null>(null);

  const editTransition = useTransition(edit, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const viewTransition = useTransition(!edit, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const voices = useSelector((state: any) => state.coaching.simulations.voices?.items || []);

  const handleEdit = () => {
    setEdit(!edit);
    setHeight(24);
    setPreviewVoice(null);
  }

  useEffect(() => {
    if (bodyBounds.height > 0) {
      setHeight(bodyBounds.height);
    }
  }, [bodyBounds, edit]);

  const handlePlay = (voice: Voice) => {
    setPreviewVoice(voice.id);
  }

  const handleStop = () => {
    setPreviewVoice(null);
  }

  const handleSelect = (voice: Voice) => {
    onChange(voice);
    setEdit(false);
  }

  return (
    <div className={cx(styles.field, styles.voice)}>
      <div className={styles.header}>
        <label>Voice</label>
        <button className={styles.editAction} onClick={handleEdit}>
          <i className={cx('paperflite-edit')} />
        </button>
      </div>
      <Spring to={{ height: height }} config={{
        duration: 300,
        friction: 14,
        tension: 120,
      }}>
        {props => (
          <animated.div style={props} className={styles.body}>
            <div className={styles.wrapper} ref={bodyRef}>
              {editTransition((style, item) =>
                item ? (
                  <animated.div style={style} className={styles.voices}>
                    {voices.map((item: Voice) => {
                      return <VoiceItem key={item.id}
                        className={styles.edit}
                        voice={item}
                        playing={previewVoice === item.id}
                        selected={voice?.id === item.id}
                        onPlay={handlePlay}
                        onStop={handleStop}
                        onSelect={handleSelect}
                      />;
                    })}
                  </animated.div>
                ) : null
              )}

              {viewTransition((style, item) =>
                item && voice ? (
                  <animated.div style={style} className={styles.selectedVoice}>
                    <VoiceItem
                      voice={voice}
                      playing={previewVoice === voice.id}
                      selected={false}
                      onPlay={handlePlay}
                      onStop={handleStop}
                    />
                  </animated.div>
                ) : null
              )}
            </div>
          </animated.div>
        )}
      </Spring>
    </div>
  );
}

interface VoiceItemProps {
  className?: string;
  voice: Voice;
  playing: boolean;
  selected: boolean;

  // eslint-disable-next-line no-unused-vars
  onPlay?: (voice: Voice) => void;

  // eslint-disable-next-line no-unused-vars
  onStop?: (voice: Voice) => void;

  // eslint-disable-next-line no-unused-vars
  onSelect?: (voice: Voice) => void;
}

const VoiceItem: React.FC<VoiceItemProps> = ({ className, voice, playing, selected, onPlay, onStop, onSelect }) => {
  const audio = useMemo(() => {
    const url = voice.previewUrl;
    const audio = new Audio();
    audio.preload = "none";
    audio.src = url;

    return audio;
  }, [voice.id]);

  const tags = useMemo(() => {
    if (!voice || _.size(voice.tags) === 0) {
      return "";
    }

    return `(${_.join(voice.tags, ", ")})`;
  }, [voice]);

  const [loaded, setLoaded] = useState(false);

  const playTransition = useTransition(!playing, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const stopTransition = useTransition(playing, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handlePlay = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (onPlay) {
      onPlay(voice);
    }
  }

  const handleStop = (e) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (onStop) {
      onStop(voice);
    }
  }

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onSelect) {
      onSelect(voice);
    }
  }

  useEffect(() => {
    audio.addEventListener('ended', () => handleStop(null));
    audio.addEventListener('pause', () => handleStop(null));
    return () => {
      audio.removeEventListener('ended', () => handleStop(null));
      audio.removeEventListener('pause', () => handleStop(null));
    };
  }, []);

  useEffect(() => {
    if (playing) {
      if (!loaded) {
        audio.load();
        audio.addEventListener('canplaythrough', function () {
          setLoaded(true);
        }, false);
      } else {
        audio.play();
      }
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [playing, loaded]);

  return (
    <div className={cx(styles.voiceItem, className, {
      [styles.selected]: selected,
    })} onClick={handleClick}>
      <div className={styles.details}>
        <div className={styles.name}>{voice.name}</div>
        <div className={styles.tags}>{voice.description}</div>
      </div>
      <div className={styles.actions}>
        {playTransition((style, item) =>
          item ? (
            <animated.button style={style} className={cx(styles.action, styles.play)} onClick={handlePlay}>
              <i className="paperflite-play" />
            </animated.button>
          ) : null
        )}

        {stopTransition((style, item) =>
          item ? (
            <animated.button style={style} className={cx(styles.action, styles.stop)} onClick={handleStop}>
              <i className="paperflite-stop" />
            </animated.button>
          ) : null
        )}

      </div>
    </div>
  );
}


const usePersonalities = () => {
  const dispatch = useDispatch();

  const { personalities, loading } = useSelector((state: any) => {
    const items = state.coaching.simulations.personalities?.items || [];
    const loading = state.coaching.simulations.personalities?.loading || false;

    return {
      personalities: items,
      loading
    }
  });


  const resolveRef = React.useRef<any>(null);
  const rejectRef = React.useRef<any>(null);

  useEffect(() => {
    dispatch(fetchPersonalities());
  }, []);

  useEffect(() => {
    if (!loading && _.size(personalities) > 0 && resolveRef.current) {
      resolveRef.current(personalities);
    }

  }, [loading, personalities]);

  return useCallback(() => {
    return new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;

      if (!loading && _.size(personalities) > 0) {
        resolve(personalities);
      }
    });
  }, [loading, personalities]);
}

interface PersonalityFieldProps {
  personality: Personality | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (personality: any) => void;
}

const PersonalityField: React.FC<PersonalityFieldProps> = ({ personality, onChange }) => {
  const personalitiesPromise = usePersonalities();

  const handlePersonalityChange = (data: any) => {
    onChange(data);
  }

  const promiseOptions = useCallback((inputValue) => {
    const promise = personalitiesPromise();

    return promise.then((personalities: any) => {
      const options = personalities.map(item => {
        // return {
        //   label: item.name,
        //   value: item.id,
        //   personality: item
        // }

        return item;
      });
      return options;
    });
  }, [personalitiesPromise]);

  return (
    <div className={cx(styles.field, styles.voice)}>
      <div className={styles.header}>
        <label>Personality</label>
      </div>
      <AsyncSelect
        className={styles.personalityMenu}
        classNamePrefix="personality-select"
        components={{
          Option: PersonalityOption,
          SingleValue: PersonalitySingleValue,
        }}
        isMulti={false}
        placeholder="e.g., Analytical, Driver, Amiable, Expressive..."
        cacheOptions
        defaultOptions
        isSearchable={false}
        value={personality}
        loadOptions={promiseOptions}
        onChange={handlePersonalityChange}
        loadingMessage={() => `Loading...`}
        noOptionsMessage={() => `No results found`}
      />
    </div>
  );
}

const PersonalityOption = (props: OptionProps<Personality>) => {
  return (
    <components.Option {...props}>
      <div className={styles.personalityOption}>
        <div className={styles.name}>{props.data.name}</div>
        <div className={styles.description}>{props.data.description}</div>
      </div>
    </components.Option>
  );
};

const PersonalitySingleValue = (props: any) => {
  return (
    <components.SingleValue {...props}>
      <div className={styles.personalityValue}>
        <div className={styles.name}>{props.data.name}</div>
        <div className={styles.description}>{props.data.description}</div>
      </div>
    </components.SingleValue>
  );
}

interface FieldLabelProps {
  label: string;
  error?: SimulationValidationError | null;
}

const FieldLabel: React.FC<FieldLabelProps> = ({ label, error }) => {
  return (
    <Tooltip placement="top-start" content={error?.message} disabled={!error}>
      <label className={cx({ [styles.error]: error })}>
        {error && <i className={cx("fa-solid", "fa-triangle-exclamation")}></i>}
        {label}
      </label>
    </Tooltip>
  );
}